import React, { ChangeEvent, useEffect, useId, useState } from "react";
// @ts-ignore
import MSGReader from "msgreader";
import config from "../../config";
import { authFetch } from "../../utils/Security/authFetch";

interface FileUploaderProps {
  value?: string[] | File[] | null;
  onChange: (files: File[] | null, uploadPaths: string[]) => void;
  // Called with the saved path when a file is deleted, so the parent can drop it from filePath
  onRemove?: (path: string) => void;
}

// Upload endpoints save as "<uuid>_<name>" (Add pages also replace spaces with "_")
const isUploadOf = (path: string, file: File) => {
  const savedName = path.replace(/\\/g, "/").split("/").pop() || "";
  return (
    savedName.endsWith("_" + file.name) ||
    savedName.endsWith("_" + file.name.replace(/\s+/g, "_"))
  );
};

const FileUploader: React.FC<FileUploaderProps> = ({
  value,
  onChange,
  onRemove,
}) => {
  const inputId = useId();
  const [uploadedFiles, setUploadedFiles] = useState<
    { file: File; url: string; path?: string }[]
  >([]);
  const [msgDataList, setMsgDataList] = useState<
    {
      subject: string;
      sender: string;
      content: string;
      file: File;
      url: string;
      path?: string;
    }[]
  >([]);

  const decodeBody = (body: any) => {
    if (typeof body === "string") return body;
    if (body instanceof Uint8Array) {
      try {
        const text = new TextDecoder("utf-8").decode(body);
        if (text.trim().startsWith("{\\rtf")) {
          return `[RTF content detected: cannot display directly]`;
        }
        return text;
      } catch {
        return "<p>Unable to decode message body</p>";
      }
    }
    return "<p>No content found</p>";
  };

  // Function to normalize file names (removes "Copy of" and other common variants)
  const normalizeFileName = (fileName: string) => {
    return fileName
      .replace(/copy of /i, "") // Remove "Copy of" (case-insensitive)
      .replace(/\s*[-_]*\d+/g, "") // Remove trailing number variants
      .trim();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg",
      "image/png",
      "application/vnd.ms-outlook",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // Word
      "application/vnd.ms-powerpoint", // PowerPoint
    ];

    const newUploadedFiles: { file: File; url: string }[] = [];
    const collectedFiles: File[] = [];
    const collectedPaths: string[] = [];

    Array.from(selectedFiles).forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();
      const isAllowedMime = allowedTypes.includes(fileType);
      const isAllowedExtension =
        /\.(pdf|xlsx|jpg|jpeg|png|msg|docx|pptx)$/i.test(fileName);

      if (!isAllowedMime && !isAllowedExtension) {
        alert(`File "${file.name}" is not allowed.`);
        return;
      }

      // Check if the file already exists in uploadedFiles based on file name and size
      const normalizedFileName = normalizeFileName(file.name.toLowerCase());
      const existingFile = uploadedFiles.some(
        (uploadedFile) =>
          normalizeFileName(uploadedFile.file.name.toLowerCase()) ===
            normalizedFileName && uploadedFile.file.size === file.size
      );
      if (existingFile) {
       //console.log(`Duplicate file detected: ${file.name}`);
        return; // Skip if the file is already uploaded
      }

      const path = `bcom/iqms/${file.name}`;
      const url = URL.createObjectURL(file);

      if (fileName.endsWith(".msg")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const arrayBuffer = event.target?.result as ArrayBuffer;
          const msgReader = new MSGReader(arrayBuffer);
          const msg = msgReader.getFileData();
          const decodedBody = decodeBody(msg.body);
          const safeContent = msg.bodyHTML || decodedBody;

          setMsgDataList((prev) => [
            ...prev,
            {
              subject: msg.subject || "No Subject",
              sender: msg.senderName || msg.senderEmail || "Unknown Sender",
              content: safeContent || "No content available",
              file,
              url,
            },
          ]);
        };
        reader.readAsArrayBuffer(file);
      } else {
        newUploadedFiles.push({ file, url });
      }

      collectedFiles.push(file);
      collectedPaths.push(path);
    });

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
    onChange(collectedFiles.length ? collectedFiles : null, collectedPaths);
  };

  const handleDeleteFile = (index: number) => {
    const fileToDelete = uploadedFiles[index];
    URL.revokeObjectURL(fileToDelete.url);
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileToDelete.path) onRemove?.(fileToDelete.path);
  };

  const handleDeleteMsgFile = (index: number) => {
    const fileToDelete = msgDataList[index];
    URL.revokeObjectURL(fileToDelete.url);
    setMsgDataList((prev) => prev.filter((_, i) => i !== index));
    if (fileToDelete.path) onRemove?.(fileToDelete.path);
  };

  const openMailClientWithMsg = (msgData: (typeof msgDataList)[number]) => {
    const subject = encodeURIComponent(msgData.subject);
    const body = encodeURIComponent(
      msgData.content.replace(/<[^>]+>/g, "").replace(/\n/g, "\r\n")
    );
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  useEffect(() => {
    if (!Array.isArray(value)) return;
    const paths = (value as (string | File)[]).filter(
      (v): v is string => typeof v === "string"
    );

    // Files just picked here: remember their saved path (so Delete can remove
    // it) instead of listing them a second time
    const isMsgUpload = (p: string) =>
      msgDataList.some((m) => m.path === p || (!m.path && isUploadOf(p, m.file)));
    setMsgDataList((prev) =>
      prev.map((m) =>
        m.path ? m : { ...m, path: paths.find((p) => isUploadOf(p, m.file)) }
      )
    );

    setUploadedFiles((prev) => {
      let next = prev;
      paths.forEach((v) => {
        if (next.some((f) => f.path === v) || isMsgUpload(v)) return;

        const local = next.find((f) => !f.path && isUploadOf(v, f.file));
        if (local) {
          next = next.map((f) => (f === local ? { ...f, path: v } : f));
          return;
        }

        const normalizedPath = v.replace(/\\/g, "/");
        const url = `${
          config.apiBaseUrl
        }/api/incidents/downloadFile?path=${encodeURIComponent(
          normalizedPath
        )}`;
        const fakeFile = new File(
          [],
          normalizedPath.split("/").pop() || "file"
        );
        next = [...next, { file: fakeFile, url, path: v }];
      });
      return next;
    });
  }, [value]);

  return (
    <div className="file-uploader">
      <label htmlFor={inputId}>
        Upload file(s) (PDF, XLSX, Images, MSG, DOCX, PPTX...)
      </label>
      <input
        type="file"
        id={inputId}
        accept=".pdf, .eml, .xlsx, .msg, .docx, .pptx, image/*"
        onChange={handleFileChange}
        multiple
      />

      {/* MSG files */}
      {msgDataList.map((msg, idx) => (
        <div key={idx} style={{ marginTop: 20 }}>
          <strong>MSG Subject:</strong> {msg.subject}
          <div style={{ display: "flex", gap: "10px", marginTop: "5px" }}>
            <a
              href={msg.url}
              download={msg.file.name}
              style={{ color: "#007bff", textDecoration: "underline" }}
            >
              Download MSG
            </a>
            <a
              href={msg.url}
              download
              style={{ color: "#007bff", textDecoration: "underline" }}
            >
              {msg.file.name}
            </a>
            <button type="button" onClick={() => handleDeleteMsgFile(idx)}>❌ Delete</button>
          </div>
        </div>
      ))}

      {uploadedFiles.map(({ file, url, path }, idx) => {
        const isFromBackend = file.size === 0 && typeof path === "string";
        const displayName = file.name.includes("_")
          ? file.name.split("_").slice(1).join("_")
          : file.name;

        const handleDownload = async () => {
          try {
            const response = await authFetch(
              `${
                config.apiBaseUrl
              }/api/incidents/downloadFile?path=${encodeURIComponent(
                path ?? ""
              )}`
            );
            if (!response.ok) throw new Error("Download failed");

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = displayName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          } catch (error) {
            alert("Failed to download file.");
            console.error(error);
          }
        };

        return (
          <div key={idx} style={{ marginTop: 20 }}>
            <strong>{displayName}</strong>
            <div style={{ marginTop: "5px" }}>
              {isFromBackend ? (
                <button type="button" onClick={handleDownload}>View File</button>
              ) : (
                <button type="button" onClick={() => window.open(url, "_blank")}>
                  View File
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDeleteFile(idx)}
                style={{ marginLeft: 10 }}
              >
                ❌ Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FileUploader;
